import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";
import TransporterServiceCard from "../components/TransporterServiceCard";

const DEFAULT_PAYMENT_METHODS = ["PAY_ON_DELIVERY", "PAYNOW", "BANK_TRANSFER"];
const PAYNOW_CHANNEL_METHODS = new Set(["PAYNOW", "ECOCASH", "ONEMONEY", "CARD"]);

const PAYMENT_METHOD_META = {
  PAY_ON_DELIVERY: {
    title: "Pay on delivery",
    short: "Buyer pays when the goods are delivered or collected.",
    detail: "TAZY keeps the payment record pending. Finance/admin confirms it after delivery payment is verified.",
  },
  BANK_TRANSFER: {
    title: "Bank transfer",
    short: "Transfer to the TAZY bank account.",
    detail: "Use the order number as reference, then enter proof/reference here for finance confirmation.",
  },
  PAYNOW: {
    title: "Paynow",
    short: "Pay directly using an EcoCash or OneMoney phone prompt.",
    detail: "Choose your mobile wallet and enter the payer's number. Your order updates after payment confirmation.",
  },
  MANUAL: {
    title: "Manual / cash / bank",
    short: "Manual payment captured by TAZY finance.",
    detail: "Use this for cash, bank or manually verified payments.",
  },
  OTHER: {
    title: "Other method",
    short: "Use another agreed payment method.",
    detail: "Add details in the payment note so finance can verify it.",
  },
};

function statusTone(value) {
  const status = String(value || "").toUpperCase();
  if (["CONFIRMED", "PROCESSING", "READY_FOR_DELIVERY", "IN_TRANSIT", "ASSIGNED"].includes(status)) return "warn";
  if (["COMPLETED", "PAID", "DELIVERED"].includes(status)) return "good";
  if (["CANCELLED", "FAILED"].includes(status)) return "bad";
  return "neutral";
}

function formatMoney(order) {
  const amount = Number(order.total_amount || 0);
  return `${order.currency || "US$"}${amount.toFixed(2)}`;
}

function methodTitle(method) {
  const key = String(method || "").toUpperCase();
  return PAYMENT_METHOD_META[key]?.title || key.replaceAll("_", " ");
}

export default function OrdersWorkspace() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [financeSettings, setFinanceSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [deliveryModal, setDeliveryModal] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [deliveryForm, setDeliveryForm] = useState({ pickup_address: "", dropoff_address: "" });
  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ payment_method: "PAY_ON_DELIVERY", paynow_method: "ecocash", mobile: "", payment_reference: "", notes: "" });
  const [deliveryPaymentModal, setDeliveryPaymentModal] = useState(null);
  const [deliveryPaymentForm, setDeliveryPaymentForm] = useState({ payment_method: "PAYNOW", paynow_method: "ecocash", mobile: "", payment_reference: "", notes: "" });

  const canProgress = ["SELLER", "BUSINESS"].includes(user?.account_type) || user?.is_platform_staff || user?.account_type === "SUPER_ADMIN";
  const isBuyer = user?.account_type === "BUYER";

  const activePaymentMethods = useMemo(() => {
    const configured = financeSettings?.active_payment_methods;
    const list = Array.isArray(configured) && configured.length ? configured : DEFAULT_PAYMENT_METHODS;
    const cleaned = list.map((item) => String(item || "").toUpperCase()).filter(Boolean).reduce((methods, method) => {
      const value = PAYNOW_CHANNEL_METHODS.has(method) ? "PAYNOW" : method;
      if (!methods.includes(value)) methods.push(value);
      return methods;
    }, []);
    return cleaned.includes("PAY_ON_DELIVERY") ? cleaned : ["PAY_ON_DELIVERY", ...cleaned];
  }, [financeSettings]);

  const deliveryPaymentMethods = useMemo(
    () => activePaymentMethods.filter((method) => method !== "PAY_ON_DELIVERY"),
    [activePaymentMethods],
  );

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [orderRes, serviceRes, financeRes] = await Promise.all([
        phase1Api.orders(),
        phase1Api.transporterServices(),
        phase1Api.financeSettings().catch(() => ({ data: null })),
      ]);
      setRows(Array.isArray(orderRes.data) ? orderRes.data : []);
      setServices(Array.isArray(serviceRes.data) ? serviceRes.data : []);
      setFinanceSettings(financeRes.data || null);
    } catch (err) {
      setError("Unable to load orders, transporters or finance settings right now.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((row) => !["COMPLETED", "CANCELLED"].includes(row.status)).length,
    ready: rows.filter((row) => row.status === "READY_FOR_DELIVERY").length,
    transit: rows.filter((row) => row.status === "IN_TRANSIT").length,
    completed: rows.filter((row) => row.status === "COMPLETED").length,
  }), [rows]);

  function openDeliveryModal(order) {
    setDeliveryModal(order);
    setSelectedService(null);
    setDeliveryForm({ pickup_address: "", dropoff_address: "" });
  }

  function openPaymentModal(order) {
    const pendingMethod = order.latest_payment?.payment_method;
    const configuredDefault = financeSettings?.default_payment_method;
    const normalisedPending = PAYNOW_CHANNEL_METHODS.has(String(pendingMethod || "").toUpperCase()) ? "PAYNOW" : pendingMethod;
    const normalisedDefault = PAYNOW_CHANNEL_METHODS.has(String(configuredDefault || "").toUpperCase()) ? "PAYNOW" : configuredDefault;
    const method = normalisedPending || (activePaymentMethods.includes(normalisedDefault) ? normalisedDefault : activePaymentMethods[0]) || "PAY_ON_DELIVERY";
    setPaymentModal(order);
    setPaymentForm({
      payment_method: method,
      paynow_method: "ecocash",
      mobile: "",
      payment_reference: order.latest_payment?.payment_reference || "",
      notes: order.latest_payment?.status === "PENDING" ? "Existing payment record is pending finance confirmation." : "",
    });
  }

  async function submitPayment(event) {
    event.preventDefault();
    if (!paymentModal?.id) return;
    setBusy(`${paymentModal.id}-PAYMENT`);
    setMessage("");
    setError("");
    try {
      const res = await phase1Api.initiateOrderPayment(paymentModal.id, {
        payment_method: paymentForm.payment_method,
        ...(paymentForm.payment_method === "PAYNOW" ? { paynow_method: paymentForm.paynow_method, mobile: paymentForm.mobile } : {}),
        payment_reference: paymentForm.payment_reference,
        notes: paymentForm.notes,
      });
      const payload = res.data || {};
      const tx = payload.payment || payload;
      if (payload.redirect_url) {
        window.location.assign(payload.redirect_url);
        return;
      }
      if (payload.mobile_pending && payload.payment_return_url) {
        window.location.assign(payload.payment_return_url);
        return;
      }
      setMessage(`${methodTitle(tx.payment_method || paymentForm.payment_method)} selected for ${paymentModal.order_number}. Payment record ${tx.code || "created"} is now pending TAZY finance confirmation.`);
      setPaymentModal(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Payment option could not be submitted.");
    } finally {
      setBusy("");
    }
  }

  function openDeliveryPayment(order) {
    const firstMethod = deliveryPaymentMethods.includes("PAYNOW") ? "PAYNOW" : deliveryPaymentMethods[0] || "";
    setDeliveryPaymentModal(order);
    setDeliveryPaymentForm({ payment_method: firstMethod, paynow_method: "ecocash", mobile: "", payment_reference: "", notes: "" });
  }

  async function submitDeliveryPayment(event) {
    event.preventDefault();
    const delivery = deliveryPaymentModal?.latest_delivery;
    if (!delivery?.id) return;
    setBusy(`${delivery.id}-TRANSPORT-PAYMENT`);
    setMessage("");
    setError("");
    try {
      const res = await phase1Api.initiateDeliveryPayment(delivery.id, {
        payment_method: deliveryPaymentForm.payment_method,
        ...(deliveryPaymentForm.payment_method === "PAYNOW" ? { paynow_method: deliveryPaymentForm.paynow_method, mobile: deliveryPaymentForm.mobile } : {}),
        payment_reference: deliveryPaymentForm.payment_reference,
        notes: deliveryPaymentForm.notes,
      });
      if (res.data?.mobile_pending && res.data?.payment_return_url) {
        window.location.assign(res.data.payment_return_url);
        return;
      }
      setMessage(`Transporter payment ${res.data?.payment?.code || "record"} is awaiting confirmation.`);
      setDeliveryPaymentModal(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Transporter payment could not be started.");
    } finally {
      setBusy("");
    }
  }

  async function checkDeliveryPayment(order) {
    const delivery = order.latest_delivery;
    if (!delivery?.payment_id) return;
    setBusy(`${delivery.payment_id}-TRANSPORT-CHECK`);
    setMessage("");
    setError("");
    try {
      const res = await phase1Api.checkPaynowPayment(delivery.payment_id);
      if (res.data?.status === "CONFIRMED") {
        setMessage(`Transporter payment ${res.data?.code || delivery.payment_code} is confirmed. The transporter can now proceed to pickup.`);
      } else {
        setMessage(`Transporter payment ${delivery.payment_code || "request"} is still awaiting approval or network confirmation.`);
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Transporter payment status could not be checked.");
    } finally {
      setBusy("");
    }
  }

  async function requestDelivery(event) {
    event.preventDefault();
    if (!deliveryModal?.id) return;
    if (!selectedService?.id) {
      setError("Choose a transporter service before opening the delivery request.");
      return;
    }
    setBusy(`${deliveryModal.id}-DELIVERY`);
    setMessage("");
    setError("");
    try {
      await phase1Api.createDelivery({
        order: deliveryModal.id,
        service: selectedService.id,
        courier: selectedService.courier,
        requested_service: selectedService.service_name,
        pickup_address: deliveryForm.pickup_address,
        dropoff_address: deliveryForm.dropoff_address,
        status: "REQUESTED",
      });
      setMessage(`Transport request sent to ${selectedService.courier_name} for ${deliveryModal.order_number}.`);
      setDeliveryModal(null);
      setSelectedService(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Transport request could not be created.");
    } finally {
      setBusy("");
    }
  }

  async function updateStatus(order, status) {
    setBusy(`${order.id}-${status}`);
    setMessage("");
    setError("");
    try {
      await phase1Api.orderStatus(order.id, { status });
      setMessage(`Order ${order.order_number} updated to ${status.replaceAll("_", " ")}.`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Order status could not be updated.");
    } finally {
      setBusy("");
    }
  }

  return (
    <div className={`portal-shell-v2 refined-portal-shell ${collapsed ? "is-collapsed" : ""}`}>
      <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed((value) => !value)} />
      <main className="portal-main-v2 records-main-clean orders-workspace-main">
        <PortalTopbar user={user} onLogout={logout} />

        <section className="orders-hero-v2 orders-hero-polished">
          <div>
            <span>ORDER OPERATIONS</span>
            <h1>{canProgress ? "Order fulfilment and transporter selection" : "My orders and delivery tracking"}</h1>
            <p>{canProgress ? "Confirm stock, prepare goods, then choose a registered transporter service for delivery fulfilment." : "Track your purchase from seller confirmation, payment selection, transporter assignment and delivery completion."}</p>
          </div>
          <div className="order-flow-kpis">
            <div><strong>{stats.total}</strong><span>Total</span></div>
            <div><strong>{stats.active}</strong><span>Active</span></div>
            <div><strong>{stats.ready}</strong><span>Ready</span></div>
            <div><strong>{stats.transit}</strong><span>Transit</span></div>
          </div>
        </section>

        {message && <div className="success-box workspace-alert">{message}</div>}
        {error && <div className="error-box workspace-alert">{error}</div>}


        <section className="records-card phase1b-records-card order-flow-card">
          <div className="records-card__header">
            <div><span>LIVE ORDERS</span><h3>{isBuyer ? "Your order timeline" : "Seller fulfilment queue"}</h3></div>
            <button type="button" onClick={load}>Refresh</button>
          </div>
          {loading ? <div className="records-empty">Loading orders...</div> : rows.length === 0 ? <div className="records-empty">No orders yet. Buyers can place orders from the public marketplace or from responded enquiries.</div> : (
            <div className="order-list-v2">
              {rows.map((order) => (
                <article className="order-card-v2" key={order.id}>
                  <div className="order-card-head-v2">
                    <div><span className={`status-chip ${statusTone(order.status)}`}>{order.status}</span><h3>{order.order_number}</h3></div>
                    <strong>{formatMoney(order)}</strong>
                  </div>
                  <div className="order-meta-grid-v2">
                    <div><span>Buyer</span><strong>{order.buyer_name || "—"}</strong></div>
                    <div><span>Seller</span><strong>{order.seller_name || "—"}</strong></div>
                    <div><span>Payment</span><strong>{order.payment_status}</strong>{order.latest_payment && <em>{methodTitle(order.latest_payment.payment_method)} • {order.latest_payment.status}</em>}</div>
                    <div><span>Delivery</span><strong>{order.delivery_status}</strong></div>
                  </div>
                  <div className="takealot-track-v2">
                    {(order.tracking_steps || []).map((step) => (
                      <div className={`track-step-v2 ${step.done ? "done" : ""} ${step.active ? "active" : ""}`} key={step.code}>
                        <b />
                        <span>{step.label}</span>
                      </div>
                    ))}
                  </div>
                  {order.items?.length ? <div className="order-items-line">{order.items.map((item) => `${item.quantity} × ${item.title || item.product_title}`).join(" • ")}</div> : null}
                  <div className="order-action-row-v2 fulfilment-action-row">
                    {isBuyer && order.payment_status !== "PAID" && (
                      <button type="button" className="pay-through-tazy-btn" disabled={busy === `${order.id}-PAYMENT`} onClick={() => openPaymentModal(order)}>{order.latest_payment?.status === "PENDING" ? "View payment instructions" : "Choose payment option"}</button>
                    )}
                    {canProgress && order.next_action ? <button type="button" disabled={busy === `${order.id}-${order.next_action.status}`} onClick={() => updateStatus(order, order.next_action.status)}>{order.next_action.label}</button> : null}
                    {order.can_cancel ? <button type="button" className="danger-ghost" disabled={busy === `${order.id}-CANCELLED`} onClick={() => updateStatus(order, "CANCELLED")}>Cancel order</button> : null}
                    {canProgress && order.can_choose_transporter && (
                      <button type="button" className="transport-request-btn" disabled={busy === `${order.id}-DELIVERY`} onClick={() => openDeliveryModal(order)}>Choose transporter</button>
                    )}
                    {canProgress && order.latest_delivery?.status === "QUOTED" && ["UNPAID", "FAILED"].includes(order.latest_delivery?.payment_status) ? <button type="button" className="pay-through-tazy-btn" onClick={() => openDeliveryPayment(order)}>Pay transporter {order.currency || "US$"}{Number(order.latest_delivery.quoted_amount || 0).toFixed(2)}</button> : null}
                    {canProgress && order.latest_delivery?.status === "QUOTED" && order.latest_delivery?.payment_status === "PENDING" && order.latest_delivery?.payment_method === "PAYNOW" ? <button type="button" className="pay-through-tazy-btn" disabled={busy === `${order.latest_delivery.payment_id}-TRANSPORT-CHECK`} onClick={() => checkDeliveryPayment(order)}>{busy === `${order.latest_delivery.payment_id}-TRANSPORT-CHECK` ? "Checking payment..." : "Check payment confirmation"}</button> : null}
                    {isBuyer && order.can_confirm_receipt ? <button type="button" className="confirm-receipt-btn" disabled={busy === `${order.id}-COMPLETED`} onClick={() => updateStatus(order, "COMPLETED")}>Confirm goods received</button> : null}
                    {isBuyer && order.can_review ? <Link className="order-review-link" to="/portal/reviews">Review completed purchase</Link> : null}
                  </div>
                  {!order.next_action && canProgress && order.status === "READY_FOR_DELIVERY" && !order.latest_delivery ? <p className="order-next-step-note">Next: choose a transporter, receive their quote, then pay the delivery charge.</p> : null}
                  {order.latest_delivery?.status === "QUOTED" && ["UNPAID", "FAILED"].includes(order.latest_delivery?.payment_status) ? <p className="order-next-step-note">Transporter quote received. The seller must pay the delivery charge before pickup.</p> : null}
                  {order.latest_delivery?.status === "QUOTED" && order.latest_delivery?.payment_status === "PENDING" ? <p className="order-next-step-note">Transporter payment is awaiting confirmation. Pickup becomes available after confirmation.</p> : null}
                  {order.delivery_status === "AWAITING_BUYER_CONFIRMATION" ? <p className="order-next-step-note">The transporter marked this delivery complete. The buyer must now confirm receipt.</p> : null}
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {paymentModal && (
        <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
          <section className="tazy-modal-card action-modal-card payment-choice-modal">
            <header className="tazy-modal-header"><div><span>PAYMENT OPTION</span><h3>Choose how to pay for {paymentModal.order_number}</h3></div><button type="button" onClick={() => setPaymentModal(null)}>×</button></header>
            <form className="payment-choice-body" onSubmit={submitPayment}>
              <div className="payment-summary-box">
                <div><span>Order total</span><strong>{formatMoney(paymentModal)}</strong></div>
                <p>{financeSettings?.payment_instructions || "Choose a payment option. TAZY records the payment as pending until finance/admin confirms money received."}</p>
              </div>
              <div className="payment-method-grid">
                {activePaymentMethods.map((method) => {
                  const meta = PAYMENT_METHOD_META[method] || { title: methodTitle(method), short: "Available payment method.", detail: "TAZY finance will verify this payment method." };
                  return (
                    <button type="button" key={method} className={paymentForm.payment_method === method ? "payment-method-card active" : "payment-method-card"} onClick={() => setPaymentForm((prev) => ({ ...prev, payment_method: method }))}>
                      <strong>{meta.title}</strong>
                      <span>{meta.short}</span>
                    </button>
                  );
                })}
              </div>
              <div className="payment-method-note">
                <strong>{methodTitle(paymentForm.payment_method)}</strong>
                <span>{PAYMENT_METHOD_META[paymentForm.payment_method]?.detail || "TAZY finance will verify this payment method."}</span>
              </div>
              {paymentForm.payment_method === "PAYNOW" && (
                <div className="paynow-checkout-fields">
                  <div>
                    <label htmlFor="paynow-checkout-method">Mobile wallet</label>
                    <select id="paynow-checkout-method" value={paymentForm.paynow_method} onChange={(e) => setPaymentForm((p) => ({ ...p, paynow_method: e.target.value }))}>
                      <option value="ecocash">EcoCash</option>
                      <option value="onemoney">OneMoney</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="paynow-mobile">Mobile number</label>
                    <input id="paynow-mobile" type="tel" inputMode="tel" required value={paymentForm.mobile} onChange={(e) => setPaymentForm((p) => ({ ...p, mobile: e.target.value }))} placeholder="0771234567" />
                    <small>A payment request will be sent to this phone for approval.</small>
                  </div>
                </div>
              )}
              {paymentForm.payment_method !== "PAYNOW" && (
                <div className="modal-form-grid polished-form-grid payment-reference-grid">
                  <div><label>Payment reference / confirmation code</label><input value={paymentForm.payment_reference} onChange={(e) => setPaymentForm((p) => ({ ...p, payment_reference: e.target.value }))} placeholder="Optional now, required after payment where applicable" /></div>
                  <div><label>Payment note</label><input value={paymentForm.notes} onChange={(e) => setPaymentForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Example: cash on delivery or bank transfer details" /></div>
                </div>
              )}
              <div className="payment-modal-actions">
                <button type="button" onClick={() => setPaymentModal(null)}>Cancel</button>
                <button type="submit" disabled={busy === `${paymentModal.id}-PAYMENT`}>{busy === `${paymentModal.id}-PAYMENT` ? "Sending request..." : paymentForm.payment_method === "PAYNOW" ? "Send payment request" : "Submit payment option"}</button>
              </div>
            </form>
          </section>
        </div>
      )}

      {deliveryModal && (
        <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
          <section className="tazy-modal-card action-modal-card transporter-picker-modal">
            <header className="tazy-modal-header"><div><span>TRANSPORTER SELECTION</span><h3>Choose delivery service for {deliveryModal.order_number}</h3></div><button type="button" onClick={() => setDeliveryModal(null)}>×</button></header>
            <form className="transporter-picker-body" onSubmit={requestDelivery}>
              <div className="selected-transport-summary">
                <div><span>Selected service</span><strong>{selectedService ? selectedService.service_name : "No transporter selected"}</strong><small>{selectedService ? `${selectedService.courier_name} • ${selectedService.base_city || "Service area"}` : "Pick one of the approved services below."}</small></div>
                <button type="submit" disabled={!selectedService || busy === `${deliveryModal.id}-DELIVERY`}>Send delivery request</button>
              </div>
              <div className="transporter-picker-grid">
                {services.length ? services.map((service) => (
                  <div className={selectedService?.id === service.id ? "selected-service-card" : ""} key={service.id}>
                    <TransporterServiceCard service={service} onSelect={setSelectedService} compact />
                  </div>
                )) : <div className="records-empty soft-empty">No approved transporter services yet. Transporters must publish services before sellers can choose them.</div>}
              </div>
              <div className="modal-form-grid polished-form-grid transport-address-grid">
                <div><label>Drop-off area</label><input value={deliveryForm.dropoff_address} onChange={(e) => setDeliveryForm((p) => ({ ...p, dropoff_address: e.target.value }))} placeholder="Customer area / pickup point" /></div>
                <div><label>Pickup address</label><input value={deliveryForm.pickup_address} onChange={(e) => setDeliveryForm((p) => ({ ...p, pickup_address: e.target.value }))} placeholder="Seller/store/warehouse pickup location" /></div>
              </div>
            </form>
          </section>
        </div>
      )}

      {deliveryPaymentModal && (
        <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
          <section className="tazy-modal-card action-modal-card payment-choice-modal">
            <header className="tazy-modal-header"><div><span>TRANSPORTER PAYMENT</span><h3>Pay delivery quote for {deliveryPaymentModal.order_number}</h3></div><button type="button" onClick={() => setDeliveryPaymentModal(null)}>×</button></header>
            <form className="payment-choice-body" onSubmit={submitDeliveryPayment}>
              <div className="payment-summary-box"><div><span>Delivery charge</span><strong>{deliveryPaymentModal.currency || "US$"}{Number(deliveryPaymentModal.latest_delivery?.quoted_amount || 0).toFixed(2)}</strong></div><p>Payment is made by the seller and held for the transporter until the buyer confirms receipt.</p></div>
              <div className="payment-method-grid">
                {deliveryPaymentMethods.map((method) => {
                  const meta = PAYMENT_METHOD_META[method] || { title: methodTitle(method), short: "Available payment method." };
                  return <button type="button" key={method} className={deliveryPaymentForm.payment_method === method ? "payment-method-card active" : "payment-method-card"} onClick={() => setDeliveryPaymentForm((current) => ({ ...current, payment_method: method }))}><strong>{meta.title}</strong><span>{meta.short}</span></button>;
                })}
              </div>
              {deliveryPaymentMethods.length === 0 ? <div className="error-box">No advance payment method is enabled. Ask TAZY support to enable Paynow or bank transfer.</div> : null}
              {deliveryPaymentForm.payment_method === "PAYNOW" ? <div className="paynow-checkout-fields"><div><label>Mobile wallet</label><select value={deliveryPaymentForm.paynow_method} onChange={(e) => setDeliveryPaymentForm((current) => ({ ...current, paynow_method: e.target.value }))}><option value="ecocash">EcoCash</option><option value="onemoney">OneMoney</option></select></div><div><label>Mobile number</label><input type="tel" required value={deliveryPaymentForm.mobile} onChange={(e) => setDeliveryPaymentForm((current) => ({ ...current, mobile: e.target.value }))} placeholder="0771234567" /></div></div> : <div className="modal-form-grid polished-form-grid payment-reference-grid"><div><label>Payment reference</label><input value={deliveryPaymentForm.payment_reference} onChange={(e) => setDeliveryPaymentForm((current) => ({ ...current, payment_reference: e.target.value }))} /></div><div><label>Payment note</label><input value={deliveryPaymentForm.notes} onChange={(e) => setDeliveryPaymentForm((current) => ({ ...current, notes: e.target.value }))} /></div></div>}
              <div className="payment-modal-actions"><button type="button" onClick={() => setDeliveryPaymentModal(null)}>Cancel</button><button type="submit" disabled={!deliveryPaymentForm.payment_method || busy === `${deliveryPaymentModal.latest_delivery?.id}-TRANSPORT-PAYMENT`}>{busy === `${deliveryPaymentModal.latest_delivery?.id}-TRANSPORT-PAYMENT` ? "Starting payment..." : "Pay transporter"}</button></div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
